import React, {useState, useEffect} from 'react';
import ScrollContainer from 'react-indiana-drag-scroll';
import {useTranslation} from 'react-i18next';

import {isTablet, useIsMobile} from '../../styles';

import {ReactComponent as ArrowIcon} from './../../assets/arrow.svg';

import GamesGrid from '../GamesGrid';
import {
    ButtonNext,
    ButtonPrev,
    Container,
    LeftControls,
    RightControls,
} from './styles';

function GamesCarousel({items, handleClickContent, ...props}) {
    const scrollContainerRef = React.useRef();
    const isMobile = useIsMobile();
    const {t} = useTranslation();

    const [currentSlide, setCurrentSlide] = useState(0);
    const [visibleSlides, setVisibleSlides] = useState(4);

    const screenWidth = window.innerWidth > 1440 ? 1440 : window.innerWidth;
    const margin = isMobile ? 50 : 80;
    const gutter = isMobile ? 8 : 16;
    let columnQuantity = 18;
    let itemColumns = 3;

    if (window.innerWidth >= 1280) {
        columnQuantity = 18;
    } else if (window.innerWidth > 1024) {
        columnQuantity = 15;
    } else if (window.innerWidth > 768) {
        columnQuantity = 12;
    } else if (window.innerWidth > 600) {
        columnQuantity = 9;
    } else {
        columnQuantity = 6;
    }

    const [columnSize, setColumnSize] = useState(
        (screenWidth - margin - gutter * (columnQuantity - 1)) / columnQuantity,
    );
    const [slideWidth, setSlideWidth] = useState(
        (itemColumns - 1) * gutter + itemColumns * columnSize,
    );
    const [gridWidth, setGridWidth] = useState(
        (slideWidth + gutter) * items.length - margin / 2,
    );

    useEffect(() => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTo(0, 0);
        }
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        handleWindowResize();

        window.addEventListener('resize', handleWindowResize);

        return () => {
            window.removeEventListener('resize', handleWindowResize);
        };
        // eslint-disable-next-line
    }, [screenWidth]);

    const handleWindowResize = () => {
        if (scrollContainerRef.current) {
            if (!isMobile && !isTablet) {
                scrollContainerRef.current.scrollLeft = 0;
            }

            // eslint-disable-next-line
            const currentWindowWidth = scrollContainerRef.current.getBoundingClientRect()
                .width;

            const slide = scrollContainerRef.current.querySelector(
                '#games-grid',
            ).children[0];

            if (!slide) return;

            const calcSlideWidth = slide.getBoundingClientRect().width;

            const calcVisibleSlides = Math.floor(
                currentWindowWidth / calcSlideWidth,
            );

            setVisibleSlides(calcVisibleSlides);

            recalculateCarouselSizes();
        }
    };

    useEffect(() => {
        const scrollContainer = scrollContainerRef.current;

        if (scrollContainer) {
            scrollContainer.addEventListener('scroll', handleContainerScroll);
        }

        return () => {
            scrollContainer &&
                scrollContainer.removeEventListener(
                    'scroll',
                    handleContainerScroll,
                );
        };

        // eslint-disable-next-line
    }, [scrollContainerRef.current, screenWidth]);

    const recalculateCarouselSizes = () => {
        const newColumnSize =
            (screenWidth - margin - gutter * (columnQuantity - 1)) /
            columnQuantity;

        const newSlideWidth =
            (itemColumns - 1) * gutter + itemColumns * newColumnSize;

        const newGridWidth =
            (newSlideWidth + gutter) * items.length - margin / 2;

        setColumnSize(newColumnSize);
        setSlideWidth(newSlideWidth);
        setGridWidth(newGridWidth);
    };

    const handleContainerScroll = event => {
        const contentWidth = slideWidth + (isMobile ? 8 : 16);

        let scrollLeft = event.target.scrollLeft + slideWidth;

        const currSlide = Math.min(
            Math.floor(scrollLeft / contentWidth),
            items.length - 1 * 2,
        );

        setCurrentSlide(currSlide);
    };

    const handlePreviousSlide = () => {
        const scrollContainer = scrollContainerRef.current;

        if (!scrollContainer) return;

        const slideSize = slideWidth + (isMobile ? 8 : 16);

        if (currentSlide > 0) {
            scrollContainerRef.current.scrollTo({
                left: (currentSlide - 1) * slideSize,
                behavior: 'smooth',
            });
        }

        props.handleClickArrow && props.handleClickArrow('previous');
    };

    const handleNextSlide = () => {
        const scrollContainer = scrollContainerRef.current;

        if (!scrollContainer) return;

        const slideSize = slideWidth + (isMobile ? 8 : 16);

        if (currentSlide < items.length) {
            scrollContainerRef.current.scrollTo({
                left: (currentSlide + 1) * slideSize,
                behavior: 'smooth',
            });
        }

        props.handleClickArrow && props.handleClickArrow('next');
    };

    return (
        <Container
            className="carousel-container"
            gridWidth={gridWidth}
            slideWidth={slideWidth}
            visibleWidth={
                scrollContainerRef.current &&
                scrollContainerRef.current.getBoundingClientRect().width
            }
        >
            <ScrollContainer
                // horizontal={horizontalScroll}
                innerRef={scrollContainerRef}
                className="scroll-container"
            >
                <GamesGrid items={items} onClickItem={handleClickContent} />
            </ScrollContainer>

            <LeftControls>
                <ButtonPrev
                    disabled={currentSlide <= 0}
                    onClick={handlePreviousSlide}
                >
                    <ArrowIcon title={t('controls.previous')} />
                </ButtonPrev>
            </LeftControls>

            <RightControls>
                <ButtonNext
                    disabled={currentSlide >= items.length - visibleSlides}
                    onClick={handleNextSlide}
                >
                    <ArrowIcon title={t('controls.next')} />
                </ButtonNext>
            </RightControls>
        </Container>
    );
}

export default GamesCarousel;
